import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  adsenseStatus, adsenseConnectUrl, adsenseSync, adsenseDisconnect, adsenseImport, adsenseManual, adsenseClear, adsenseScan, adsenseScans,
} from '../api/modulesApi';
import { errorMessage } from '../api/securityApi';
import ModuleNav from '../components/Modules/ModuleNav';
import RequirePermission from '../auth/RequirePermission';

const today = () => new Date().toISOString().slice(0, 10);

function ScanResult({ scan }) {
  let checks = [];
  try { checks = JSON.parse(scan.resultJson || '[]'); } catch { /* ignore */ }
  const color = scan.score >= 80 ? '#047857' : scan.score >= 60 ? '#b45309' : '#b91c1c';
  return (
    <div className="sec-card" style={{ marginTop: 12 }}>
      <div className="sec-row" style={{ justifyContent: 'space-between' }}>
        <b className="sec-mono">{scan.url}</b>
        <span style={{ fontSize: 28, fontWeight: 700, color }}>{scan.score}/100</span>
      </div>
      {checks.map((c, i) => (
        <div className="mod-check" key={i}>
          <span className={`mod-dot ${c.ok ? 'ok' : 'warn'}`} />
          <div><b>{c.name}</b><div className="muted">{c.detail}</div></div>
        </div>
      ))}
    </div>
  );
}

export default function AdSensePage() {
  const [sp] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [scans, setScans] = useState([]);
  const [scanUrl, setScanUrl] = useState('');
  const [scan, setScan] = useState(null);
  const [manual, setManual] = useState({ date: today(), domain: '', pageViews: '', impressions: '', clicks: '', earnings: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(sp.get('error') || '');
  const [info, setInfo] = useState(sp.get('connected') ? 'Google account connected. Press “Sync now” to load data.' : '');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const [s, sc] = await Promise.all([adsenseStatus(), adsenseScans()]); setStatus(s); setScans(sc); }
    catch (e) { setError(errorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setError(''); setInfo(''); setBusy(true);
    try { const r = await fn(); if (ok) setInfo(typeof ok === 'function' ? ok(r) : ok); await load(); return r; }
    catch (e) { setError(errorMessage(e)); return null; }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { setError('File is larger than 15 MB.'); return; }
    const text = await f.text();
    act(() => adsenseImport(text), (r) => `Imported ${r.rows} rows (${r.skipped} skipped) from ${String(r.from).slice(0, 10)} to ${String(r.to).slice(0, 10)}.`);
  };

  return (
    <div className="shell">
      <header className="page-header"><div><h1>AdSense data & compliance</h1>
        <p className="muted">Bring your earnings in, then use the AdSense reports (earnings, RPM, forecast, revenue at risk, placement) under <Link to="/modules">All modules</Link>.</p></div></header>
      <ModuleNav />
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}

      <div className="mod-two">
        <div className="sec-card">
          <h3>Your data</h3>
          {status && <p>{status.rows.toLocaleString()} daily rows{status.from ? <> from <b>{String(status.from).slice(0, 10)}</b> to <b>{String(status.to).slice(0, 10)}</b></> : ''}.</p>}
          <RequirePermission code="workspace.write">
            <div className="sec-row">
              <button className="primary" disabled={busy} onClick={() => fileRef.current?.click()}>Import AdSense CSV</button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
              <button className="ghost danger" disabled={busy} onClick={() => window.confirm('Delete all imported AdSense rows?') && act(() => adsenseClear(), 'Data removed.')}>Clear data</button>
            </div>
            <p className="mod-note">In AdSense: Reports → create a report with Date, Site, Country, Ad unit and the metrics Page views, Ad requests, Impressions, Clicks, Estimated earnings → Download CSV. Re-importing a date range replaces that range.</p>
          </RequirePermission>
        </div>

        <div className="sec-card">
          <h3>Google connection</h3>
          {status && !status.oauthConfigured && <p className="muted">Google sign-in is not configured on the server, so only CSV import is available. An administrator can enable it by setting the Google client id, secret and redirect URL.</p>}
          {status?.connection ? (
            <>
              <p><b>{status.connection.displayName}</b> <span className="mod-chip">{status.connection.status}</span></p>
              {status.connection.lastError && <p style={{ color: '#b91c1c' }}>{status.connection.lastError}</p>}
              <p className="muted">Last sync: {status.connection.lastSyncAt ? new Date(status.connection.lastSyncAt).toLocaleString() : 'never'}</p>
              <RequirePermission code="workspace.write">
                <div className="sec-row">
                  <button className="primary" disabled={busy} onClick={() => act(() => adsenseSync(30), (r) => r.message)}>Sync now (30 days)</button>
                  <button className="ghost danger" disabled={busy} onClick={() => act(adsenseDisconnect, 'Disconnected.')}>Disconnect</button>
                </div>
              </RequirePermission>
            </>
          ) : status?.oauthConfigured && (
            <RequirePermission code="workspace.write">
              <button className="primary" disabled={busy} onClick={async () => { try { window.location.href = (await adsenseConnectUrl()).url; } catch (e) { setError(errorMessage(e)); } }}>Connect Google AdSense</button>
              <p className="mod-note">Read-only access to your AdSense reports.</p>
            </RequirePermission>
          )}
        </div>

        <RequirePermission code="workspace.write">
          <form className="sec-card mod-form" onSubmit={(e) => {
            e.preventDefault();
            act(() => adsenseManual({ date: manual.date, domain: manual.domain, pageViews: Number(manual.pageViews) || 0, impressions: Number(manual.impressions) || 0, clicks: Number(manual.clicks) || 0, earnings: Number(manual.earnings) || 0 }), 'Day saved.');
          }}>
            <h3>Add a day manually</h3>
            <label>Date<input className="sec-input" type="date" required value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} /></label>
            <label>Site<input className="sec-input" value={manual.domain} onChange={(e) => setManual({ ...manual, domain: e.target.value })} placeholder="example.com" /></label>
            {['pageViews', 'impressions', 'clicks', 'earnings'].map((k) => (
              <label key={k}>{{ pageViews: 'Page views', impressions: 'Impressions', clicks: 'Clicks', earnings: 'Estimated earnings' }[k]}
                <input className="sec-input" type="number" min="0" step={k === 'earnings' ? '0.01' : '1'} value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} /></label>))}
            <button className="primary" type="submit" disabled={busy}>Save day</button>
          </form>
        </RequirePermission>

        <div className="sec-card mod-wide">
          <h3>Policy compliance scan</h3>
          <p className="muted">Fetches a public page and checks things AdSense reviewers look for: HTTPS, privacy policy, contact page, ads.txt, content length, ad density, wording that asks for clicks.</p>
          <RequirePermission code="workspace.write">
            <form className="sec-row" onSubmit={async (e) => { e.preventDefault(); const r = await act(() => adsenseScan(scanUrl)); if (r) setScan(r); }}>
              <input className="sec-input" style={{ minWidth: 320 }} required placeholder="https://example.com/some-article" value={scanUrl} onChange={(e) => setScanUrl(e.target.value)} />
              <button className="primary" type="submit" disabled={busy}>{busy ? 'Scanning…' : 'Scan page'}</button>
            </form>
          </RequirePermission>
          {scan && <ScanResult scan={scan} />}
          {scans.length > 0 && (
            <table className="sec-table" style={{ marginTop: 14 }}>
              <thead><tr><th>When</th><th>URL</th><th>Score</th><th /></tr></thead>
              <tbody>{scans.slice(0, 10).map((s) => (
                <tr key={s.id}><td>{new Date(s.createdAt).toLocaleString()}</td><td className="sec-mono">{s.url}</td><td>{s.score}</td><td><button className="ghost" onClick={() => setScan(s)}>View</button></td></tr>))}</tbody>
            </table>
          )}
          <p className="mod-note">A good score does not guarantee approval; it only catches common, mechanical problems.</p>
        </div>
      </div>
    </div>
  );
}
